// ── Live-cursor / firefly popup cluster ──
// Extracted verbatim from MapCanvas.tsx: the five popup useState pairs, the two
// "latest request" refs used to discard stale responses, the useViewportSharing
// wiring and the `fetchCursorUserInfo` module helper (which had no other
// consumer). The whole block moved together, so useViewportSharing keeps its
// position relative to the surrounding effects.
//
// Bodies are byte-identical to the originals; `viewportRef` and the user id are
// parameters under their ORIGINAL names.
import { useRef, useState, type MutableRefObject } from 'react';
import { getCursorUserInfoCursorUserInfoTgUserIdGet, type CursorUserInfo } from '../../client';
import { useViewportSharing } from '../../hooks/useViewportSharing';

async function fetchCursorUserInfo(tgUserId: string | number): Promise<CursorUserInfo> {
  const { data } = await getCursorUserInfoCursorUserInfoTgUserIdGet({
    path: { tg_user_id: typeof tgUserId === 'string' ? Number(tgUserId) : tgUserId },
    throwOnError: true,
  });
  return data;
}

export interface UseCursorPopupsParams {
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  /** `userData?.user_id` — by value, re-read every render. */
  userId: string | number | null | undefined;
}

export interface UseCursorPopupsResult {
  cursorPopup: CursorUserInfo | null;
  setCursorPopup: React.Dispatch<React.SetStateAction<CursorUserInfo | null>>;
  cursorPopupLoading: boolean;
  setCursorPopupLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fireflyPopup: CursorUserInfo | null;
  setFireflyPopup: React.Dispatch<React.SetStateAction<CursorUserInfo | null>>;
  fireflyPopupLoading: boolean;
  setFireflyPopupLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fireflyRecordedAt: number | null;
}

export function useCursorPopups({ sceneRef, userId }: UseCursorPopupsParams): UseCursorPopupsResult {
  // ── Cursor popup state ───────────────────────────────────────────────────
  const [cursorPopup, setCursorPopup] = useState<CursorUserInfo | null>(null);
  const [cursorPopupLoading, setCursorPopupLoading] = useState(false);
  const [fireflyPopup, setFireflyPopup] = useState<CursorUserInfo | null>(null);
  const [fireflyPopupLoading, setFireflyPopupLoading] = useState(false);
  const [fireflyRecordedAt, setFireflyRecordedAt] = useState<number | null>(null);
  // Track the latest request to discard stale responses (prevents "wrong person" bug).
  const latestCursorReqRef  = useRef<string | null>(null);
  const latestFireflyReqRef = useRef<string | null>(null);

  // useViewportSharing still expects a RefObject; mirror the live scene viewport.
  // eslint-disable-next-line react-hooks/refs -- bridge only; body of useViewportSharing reads .current in effects
  const viewportRef = useRef(sceneRef.current.pixi.viewport);
  // eslint-disable-next-line react-hooks/refs -- keep bridge fresh each render (same as former viewportRef pass-through)
  viewportRef.current = sceneRef.current.pixi.viewport;

  useViewportSharing(
    viewportRef,
    userId,
    'main',
    (uid) => {
      latestCursorReqRef.current = uid;
      setCursorPopup(null);
      setCursorPopupLoading(true);
      fetchCursorUserInfo(uid)
        .then(info => {
          if (latestCursorReqRef.current !== uid) return; // stale
          setCursorPopup(info);
          setCursorPopupLoading(false);
        })
        .catch(() => {
          if (latestCursorReqRef.current === uid) setCursorPopupLoading(false);
        });
    },
    (fireflyId, recordedAt) => {
      latestFireflyReqRef.current = fireflyId;
      setFireflyPopup(null);
      setFireflyPopupLoading(true);
      setFireflyRecordedAt(recordedAt);
      fetchCursorUserInfo(fireflyId)
        .then(info => {
          if (latestFireflyReqRef.current !== fireflyId) return; // stale
          setFireflyPopup(info);
          setFireflyPopupLoading(false);
        })
        .catch(() => {
          if (latestFireflyReqRef.current === fireflyId) setFireflyPopupLoading(false);
        });
    },
  );

  return {
    cursorPopup,
    setCursorPopup,
    cursorPopupLoading,
    setCursorPopupLoading,
    fireflyPopup,
    setFireflyPopup,
    fireflyPopupLoading,
    setFireflyPopupLoading,
    fireflyRecordedAt,
  };
}
